import React, { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import CandidateFormSections from '../components/CandidateFormSections';
import { FieldRow } from '../components/FormSection';
import PasswordField from '../components/PasswordField';
import TermsCheckbox from '../components/TermsCheckbox';
import { Alert, SubmitButton } from '../components/Feedback';
import { usePspAuth } from '../context/PspAuthContext';
import { useCandidateForm } from '../hooks/useCandidateForm';
import { isStrongPassword, TERMS_VERSION } from '../constants/options';
import { isValidEmail } from '../utils/masks';
import { candidateApi } from '../services/pspApi';

export const PENDING_EMAIL_KEY = 'psp_pending_email';

const Register = () => {
    const { friendly_url } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = usePspAuth();
    const base = `/processos-seletivos/${friendly_url}`;

    const { values, errors, setErrors, setField } = useCandidateForm();
    const [secrets, setSecrets] = useState({ password: '', confirm_password: '' });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    if (isAuthenticated) {
        return <Navigate to={`${base}/home`} replace />;
    }

    const handleSecret = (name, value) => {
        setSecrets((current) => ({ ...current, [name]: value }));
        setErrors((current) => {
            const next = { ...current };
            delete next[name];
            return next;
        });
    };

    const validate = () => {
        const found = {};
        if (!isValidEmail(values.email)) found.email = 'Informe um e-mail válido.';
        if (!isStrongPassword(secrets.password)) {
            found.password = 'A senha não atende aos requisitos.';
        }
        if (secrets.password !== secrets.confirm_password) found.confirm_password = 'As senhas não conferem.';
        if (!termsAccepted) found.terms_accepted = 'É necessário aceitar os termos de uso.';
        return found;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage('');

        const localErrors = validate();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            setMessage('Verifique os campos informados.');
            return;
        }

        setLoading(true);
        const result = await candidateApi.register({
            ...values,
            password: secrets.password,
            confirm_password: secrets.confirm_password,
            terms_accepted: termsAccepted,
            terms_version: TERMS_VERSION
        });
        setLoading(false);

        if (result && result.status === 1) {
            sessionStorage.setItem(PENDING_EMAIL_KEY, values.email.trim().toLowerCase());
            navigate(`${base}/verificar-email`, { state: { email: values.email.trim().toLowerCase() } });
            return;
        }

        setErrors((result && result.errors) || {});
        setMessage((result && result.message) || 'Não foi possível concluir o cadastro.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const accessRows = (
        <>
            <FieldRow index={3} label="Senha" required htmlFor="psp-password">
                <PasswordField name="password" value={secrets.password} onChange={handleSecret} error={errors.password} required showRules autoComplete="new-password" />
            </FieldRow>
            <FieldRow index={4} label="Confirmar Senha" required htmlFor="psp-confirm_password">
                <PasswordField name="confirm_password" value={secrets.confirm_password} onChange={handleSecret} error={errors.confirm_password} required autoComplete="new-password" />
            </FieldRow>
        </>
    );

    return (
        <div className="psp-container psp-fade-in">
            <button type="button" className="psp-link psp-link--spaced" onClick={() => navigate(`${base}/login`)}>
                <i className="fas fa-arrow-left" aria-hidden="true" /> Voltar para Login
            </button>

            <div className="psp-form-card">
                <div className="psp-form-card__header">
                    <h1>Criar Conta de Candidato</h1>
                    <p>
                        Preencha os dados abaixo para se cadastrar. Campos marcados com <span className="psp-required">*</span> são
                        obrigatórios.
                    </p>
                </div>

                <form className="psp-form-card__body" onSubmit={handleSubmit} noValidate>
                    <Alert type="error">{message}</Alert>
                    <CandidateFormSections values={values} errors={errors} onChange={setField} accessChildren={accessRows} />
                    <section>
                        <TermsCheckbox accepted={termsAccepted} onChange={(v) => { setTermsAccepted(v); setErrors((c) => ({ ...c, terms_accepted: undefined })); }} error={errors.terms_accepted} />
                    </section>
                    <SubmitButton loading={loading}>Criar Conta</SubmitButton>
                </form>
            </div>
        </div>
    );
};

export default Register;
